import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowDown, Sparkles, Phone, MessageCircle } from "lucide-react";
import { MaskedLine } from "@/components/Reveal";
import { BRAND } from "@/data/site";

const TEL = BRAND.phone.replace(/\s/g, "");
const WA = BRAND.whatsapp.replace(/[^\d]/g, "");

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imageScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const overlayY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 mb-8">
          <span className="h-px w-8 bg-slate-300" />
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="text-xs uppercase tracking-[0.24em] text-slate-500 font-medium flex items-center gap-2"
          >
            <Sparkles className="w-3.5 h-3.5 text-blue-600" strokeWidth={1.5} />
            Est. 2016 · Mumbai — Pan India
          </motion.div>
        </div>

        <h1
          data-testid="hero-heading"
          className="font-display font-light tracking-tight text-slate-900 text-[13vw] md:text-[8.5vw] leading-[0.95] max-w-6xl"
        >
          <div className="block">
            <MaskedLine delay={0.05}>India's most</MaskedLine>
          </div>
          <div className="block">
            <MaskedLine delay={0.18}>
              <span className="font-serif-editorial italic font-normal text-slate-900/90">trusted</span>{" "}
              business
            </MaskedLine>
          </div>
          <div className="block">
            <MaskedLine delay={0.31}>
              database, on demand.
            </MaskedLine>
          </div>
        </h1>

        <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.9, ease: [0.2, 0.7, 0.15, 1] }}
            className="md:col-span-6 md:col-start-1 text-lg md:text-xl text-slate-600 leading-relaxed max-w-xl"
          >
            Access customized <span className="text-slate-900">Business, B2B, B2C, GST Sales Purchase Data, Decision Maker,
            Industry and Consumer</span> databases across India — including B2B leads database India and verified company data —
            composed for your ICP, delivered in Excel and CSV, priced transparently.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.9, ease: [0.2, 0.7, 0.15, 1] }}
            className="md:col-span-5 md:col-start-8 flex flex-wrap items-center gap-3"
          >
            <Link
              to="/contact"
              data-testid="hero-cta-quote"
              className="group inline-flex items-center gap-2 rounded-full bg-slate-900 text-white pl-6 pr-5 py-4 text-sm font-medium tracking-wide transition-all duration-300 hover:bg-blue-600 hover:shadow-xl hover:shadow-blue-500/20 active:scale-95"
            >
              Get Quote
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.75} />
            </Link>
            <a
              href={`tel:${TEL}`}
              data-testid="hero-cta-call"
              aria-label="Call Datorra India now"
              className="group inline-flex items-center gap-2 rounded-full bg-white text-slate-900 border border-slate-200 pl-6 pr-5 py-4 text-sm font-medium tracking-wide transition-all duration-300 hover:border-slate-900 hover:bg-slate-50 active:scale-95"
            >
              <Phone className="w-4 h-4 text-blue-600" strokeWidth={1.75} />
              Call Now
            </a>
            <a
              href={`https://wa.me/${WA}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-whatsapp"
              aria-label="Chat on WhatsApp"
              className="group inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white pl-6 pr-5 py-4 text-sm font-medium tracking-wide transition-all duration-300 hover:brightness-95 hover:shadow-xl hover:shadow-green-500/20 active:scale-95"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={1.75} />
              WhatsApp Now
            </a>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 mt-20 md:mt-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <motion.div
            style={{ y: imageY, scale: imageScale }}
            className="md:col-span-8 relative rounded-3xl overflow-hidden border border-slate-100 aspect-[16/10] md:aspect-[16/8] bg-slate-50"
          >
            <img
              src="https://images.unsplash.com/photo-1483366774565-c783b9f70e2c?auto=format&fit=crop&w=2000&q=80"
              alt="Business database analytics dashboard — verified data provider India"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-white/40 via-transparent to-transparent" />
            <motion.div
              style={{ y: overlayY }}
              className="absolute left-6 bottom-6 md:left-10 md:bottom-10 max-w-xs bg-white/90 backdrop-blur-md rounded-2xl p-5 border border-slate-100 shadow-lg shadow-slate-900/5"
            >
              <div className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold">Live dataset</div>
              <div className="mt-2 font-display text-2xl text-slate-900 leading-tight">
                4,82,301 <span className="text-slate-400 font-light">GST-verified</span> records
              </div>
              <div className="mt-1 text-xs text-slate-500">Refreshed this morning · 06:12 IST</div>
            </motion.div>
          </motion.div>

          <div className="md:col-span-4 flex flex-col gap-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.9 }}
              className="rounded-3xl overflow-hidden border border-slate-100 aspect-square bg-slate-50 relative"
            >
              <img
                src="https://images.pexels.com/photos/97080/pexels-photo-97080.jpeg?auto=compress&cs=tinysrgb&w=1200"
                alt="Real-time data analytics visualization for B2B lead generation"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.35, duration: 0.9 }}
              className="rounded-3xl bg-slate-900 text-white p-6 flex-1 flex flex-col justify-between min-h-[180px]"
            >
              <div className="text-xs uppercase tracking-[0.24em] text-blue-300">Trust</div>
              <div>
                <div className="font-display text-3xl font-light leading-tight">
                  1,200+ teams<br /> in India ship faster with Datorra.
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="max-w-7xl mx-auto px-6 md:px-10 mt-14 flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-slate-400"
      >
        <ArrowDown className="w-4 h-4 animate-bounce text-slate-400" strokeWidth={1.5} />
        Scroll to explore
      </motion.div>
    </section>
  );
}
